import moment from 'moment';
import i18n from '../../../../i18n';
import { Device } from '../../../../queries/ats/playback/devices/types';

export function getDeletableDevices(devices: Device[]) {
  return devices.filter((device) => device.isDeletable);
}

export function getClosestDeletableDevice(devices: Device[]) {
  const initialTime = '0001-01-01T00:00:00Z';
  const target = new Date().getTime();

  if (devices.some((device) => device.isDeletable && device.isDeletableFrom !== initialTime)) {
    throw new Error('There are deletable devices, please replace one of them.');
  }

  const devicesEligibleForDeregistration = devices.filter((device) => device.isDeletableFrom !== initialTime);

  const device = devicesEligibleForDeregistration.reduce((prev, curr) => {
    const prevDiff = Math.abs(new Date(prev.isDeletableFrom).getTime() - target);
    const currDiff = Math.abs(new Date(curr.isDeletableFrom).getTime() - target);
    return currDiff < prevDiff ? curr : prev;
  });

  return device;
}

export function getLocalizedRemainingTimeForNextSwap(devices: Device[]) {
  return i18n.t('swap_limit_reached_message', {
    ns: 'translations',
    timeRemaining: getRemainingTimeText(devices),
  });
}

function getRemainingTimeText(devices: Device[]) {
  const device = getClosestDeletableDevice(devices);

  const remainingTime = moment(device.isDeletableFrom);
  const remainingDays = Math.floor(remainingTime.diff(new Date(), 'days'));

  if (remainingDays >= 1) {
    const pluralDaysText = `${remainingDays} ${i18n.t(remainingDays > 1 ? 'day_other' : 'day_one', {
      ns: 'translations',
    })}`;
    const pluralHoursText = getRemainingHoursTextPluralText(remainingTime);

    return `${pluralDaysText} ${pluralHoursText}`;
  }

  const remainingHours = remainingTime.subtract(remainingDays, 'days').diff(new Date(), 'hours');

  return remainingHours > 1
    ? getRemainingHoursTextPluralText(remainingTime)
    : getRemainingMinutesTextPluralText(remainingTime);
}

function getRemainingHoursTextPluralText(remainingTime: moment.Moment) {
  const remainingDays = Math.floor(remainingTime.diff(new Date(), 'days'));
  const remainingHours = remainingTime.subtract(remainingDays, 'days').diff(new Date(), 'hours');

  if (remainingHours < 1) {
    return getRemainingMinutesTextPluralText(remainingTime);
  }

  return `${i18n.t(remainingHours > 1 ? 'hours-other' : 'hours-one', {
    ns: 'time',
    count: remainingHours,
  })}`;
}

function getRemainingMinutesTextPluralText(remainingTime: moment.Moment) {
  const remainingDays = Math.floor(remainingTime.diff(new Date(), 'days'));
  const remainingHours = remainingTime.subtract(remainingDays, 'days').diff(new Date(), 'hours');
  const remainingMinutes = remainingTime
    .subtract(remainingDays, 'days')
    .subtract(remainingHours, 'hours')
    .diff(new Date(), 'minutes');

  return `${i18n.t(remainingMinutes > 1 ? 'minutes-other' : 'minutes-one', {
    ns: 'time',
    count: remainingMinutes,
  })}`;
}
